import { closeQAAssistant } from './actions/closeQAAssistant';
import { collapseQAAssistant } from './actions/collapseQAAssistant';
import {
  CONTAINER_ID_PREFIX,
  DROP_ZONE_ID,
  LOGO_ID,
  LOGO_MOVING_CLASS,
  SESSION_LOGO_POSITION,
  MOVEMENT_STICKY_DISTANCE_PX,
} from './constants';
import { findTouchById, getDistance2D } from './helpers';

type PointerPosition = { x: number; y: number };
type OriginalLogoStyle = { transform: string; transition: string };

const BODY_DRAGGING_CLASS = `${CONTAINER_ID_PREFIX}body-bubble-dragging`;

const startLogoMove = (logo: HTMLElement) => {
  const { body } = document;
  body.classList.add(BODY_DRAGGING_CLASS);
  logo.classList.add(LOGO_MOVING_CLASS);
};

const getOriginalLogoState = (logo: HTMLElement) => {
  const { bottom: originalLogoPositionBottomFromTop, left: originalLogoPositionLeft } = logo.getBoundingClientRect();
  const originalLogoPositionBottom = window.innerHeight - originalLogoPositionBottomFromTop;
  const originalLogoPosition = { left: originalLogoPositionLeft, bottom: originalLogoPositionBottom };

  // Save original styles to reset
  const { transform, transition } = logo.style;
  const originalLogoStyle: OriginalLogoStyle = { transform, transition };

  return { position: originalLogoPosition, style: originalLogoStyle };
};

const moveLogo = (
  logo: HTMLElement,
  originalPosition: { left: number; bottom: number },
  startingPointerPosition: PointerPosition,
  currentPointerPosition: PointerPosition,
  isNearDropZone?: boolean
) => {
  const pointerPositionVariation = {
    x: currentPointerPosition.x - startingPointerPosition.x,
    y: currentPointerPosition.y - startingPointerPosition.y,
  };
  const newLeft = isNearDropZone ? 'calc(50% - 5px)' : `${originalPosition.left + pointerPositionVariation.x}px`;
  const newBottomPx = originalPosition.bottom - pointerPositionVariation.y;
  const newBottomPercentage = (100 * newBottomPx) / window.innerHeight;
  const newBottom = isNearDropZone ? '18px' : `${newBottomPercentage}%`;
  const newTransform = isNearDropZone ? `scale(0.8) translateX(-50%)` : 'scale(0.8)';

  logo.style.setProperty('left', newLeft, 'important');
  logo.style.setProperty('right', 'unset', 'important');
  logo.style.setProperty('bottom', newBottom, 'important');
  logo.style.setProperty('transform', newTransform, 'important');
  logo.style.setProperty('transition', 'unset', 'important');
};

const dropLogo = (
  logo: HTMLElement,
  originalLogoStyle: OriginalLogoStyle,
  currentPointerPositionX: number,
  dropZone?: HTMLElement
) => {
  // Keep bottom positioning, with a 24px margin
  const currentBottomPercentage = logo.style.bottom;
  const bottom = `min(max(${currentBottomPercentage}, 24px), calc(100% - 48px - 24px))`;
  // Horizontally stick to the edge of the current side
  const isOnLeftSide = currentPointerPositionX <= window.innerWidth / 2;
  const left = isOnLeftSide ? '24px' : 'calc(100% - 48px - 24px)';
  const right = isOnLeftSide ? 'unset' : 'unset';
  // Set styles
  logo.style.setProperty('bottom', bottom);
  logo.style.setProperty('left', left);
  logo.style.setProperty('right', right);
  // Store position in session storage
  try {
    sessionStorage.setItem(SESSION_LOGO_POSITION, JSON.stringify({ bottom, left, right }));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // Ignore error (handled in iframe)
  }
  const animationStyle = document.getElementById('animationAppearStyle');
  if (animationStyle) {
    animationStyle.innerHTML = getAppearAnimation({ bottom, left, right });
  }

  // reset other styles when dropped
  logo.style.setProperty('transform', originalLogoStyle.transform);
  logo.style.setProperty('transition', originalLogoStyle.transition);

  // Reset long press related styles
  logo.classList.remove(LOGO_MOVING_CLASS);
  if (dropZone) dropZone.classList.remove(LOGO_MOVING_CLASS);
  const { body } = document;
  body.classList.remove(BODY_DRAGGING_CLASS);
};

const expandQAAssistant = () => {
  collapseQAAssistant(false);
};
export const configureLogoBubble = (container: HTMLDivElement) => {
  const logo = container.querySelector<HTMLElement>(`#${LOGO_ID}`)!;

  // Global styles
  const style = document.createElement('style');
  container.appendChild(style);
  const { sheet } = style;
  sheet!.insertRule(`
      .${BODY_DRAGGING_CLASS} {
        user-select: none;
        -moz-user-select: none;
        -webkit-user-select: none;
        pointer-events: none;
        touch-action: none;
      }
    `);

  // Touch case
  const touchStartHandler = (e: TouchEvent) => {
    // Prevent scrolling into page when dragging logo
    e.preventDefault();

    const { identifier, clientX, clientY } = e.targetTouches[0];
    const startingTouch = { identifier, x: clientX, y: clientY };

    const state = { isNearDropZone: false, hasMovementStarted: false };

    const dropZone = document.getElementById(DROP_ZONE_ID)!;
    const {
      x: dropZoneX,
      y: dropZoneY,
      height: dropZoneHeight,
      width: dropZoneWidth,
    } = dropZone.getBoundingClientRect();
    const [dropZoneCenterX, dropZoneCenterY] = [dropZoneX + dropZoneHeight / 2, dropZoneY + dropZoneWidth / 2];

    const logo = document.getElementById(LOGO_ID)!;
    const { position: originalLogoPosition, style: originalLogoStyle } = getOriginalLogoState(logo);

    const touchMoveHandler = (e: TouchEvent) => {
      const touch = findTouchById(e.changedTouches, identifier);

      if (touch) {
        const { clientX, clientY } = touch;
        const pointerPosition = { x: clientX, y: clientY };

        // To differentiate click and movement, we keep a safety sticky zone around starting position
        // Movement only starts when the pointer is at a certain distance of the original position
        if (!state.hasMovementStarted && getDistance2D(startingTouch, pointerPosition) > MOVEMENT_STICKY_DISTANCE_PX) {
          startLogoMove(logo);
          dropZone.classList.add(LOGO_MOVING_CLASS);
          state.hasMovementStarted = true;
        }
        if (state.hasMovementStarted) {
          // If near the drop zone, stick the element to it
          // Compute distance between current pointer position and center of drop zone
          // Considered "near" the drop zone if it is around
          state.isNearDropZone =
            getDistance2D({ x: dropZoneCenterX, y: dropZoneCenterY }, pointerPosition) < dropZoneWidth * 1.5;

          moveLogo(logo, originalLogoPosition, startingTouch, pointerPosition, state.isNearDropZone);
        }
      }
    };

    const touchEndHandler = (e: TouchEvent) => {
      const touch = findTouchById(e.changedTouches, identifier);
      if (touch) {
        if (state.hasMovementStarted) {
          dropLogo(logo, originalLogoStyle, touch.clientX, dropZone);
        } else {
          expandQAAssistant();
        }

        // Clean the touch listeners
        document.removeEventListener('touchmove', touchMoveHandler);
        document.removeEventListener('touchend', touchEndHandler);

        if (state.isNearDropZone) {
          closeQAAssistant();
        }
      }
    };

    document.addEventListener('touchmove', touchMoveHandler);
    document.addEventListener('touchend', touchEndHandler);
    document.addEventListener('touchcancel', touchEndHandler);
  };
  logo.addEventListener('touchstart', touchStartHandler);

  // Mouse case
  const mouseDownHandler = ({ clientX, clientY }: MouseEvent) => {
    const startingMouse = { x: clientX, y: clientY };

    const logo = document.getElementById(LOGO_ID)!;
    const { position: originalLogoPosition, style: originalLogoStyle } = getOriginalLogoState(logo);

    let hasMovementStarted = false;
    const mouseMoveHandler = ({ clientX, clientY }: MouseEvent) => {
      const pointerPosition = { x: clientX, y: clientY };
      // To differentiate click and movement, we keep a safety sticky zone around starting position
      // Movement only starts when the pointer is at a certain distance of the original position
      if (!hasMovementStarted && getDistance2D(startingMouse, pointerPosition) > MOVEMENT_STICKY_DISTANCE_PX) {
        startLogoMove(logo);
        hasMovementStarted = true;
      }
      if (hasMovementStarted) {
        moveLogo(logo, originalLogoPosition, startingMouse, pointerPosition);
      }
    };
    const mouseUpHandler = ({ clientX }: MouseEvent) => {
      if (hasMovementStarted) {
        dropLogo(logo, originalLogoStyle, clientX);
      } else {
        expandQAAssistant();
      }

      // Clean the mouse listeners
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };
  logo.addEventListener('mousedown', mouseDownHandler);

  logo.addEventListener('contextmenu', (e: MouseEvent) => {
    // Prevent context menu on long press
    e.preventDefault();
    return false;
  });
};

export type LogoPosition = { bottom: string; left: string; right: string };
const DEFAULT_LOGO_POSITION: LogoPosition = { bottom: '24px', left: '24px', right: 'unset' };
export const getLogoPosition = (): LogoPosition => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_LOGO_POSITION) || 'null') || DEFAULT_LOGO_POSITION;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // Ignore error (handled in application)
    return DEFAULT_LOGO_POSITION;
  }
};

export const getAppearAnimation = (
  position: { left?: string; right?: string; bottom?: string } = {
    left: undefined,
    right: undefined,
    bottom: undefined,
  }
) => {
  const logoPosition = getLogoPosition();
  const { left = logoPosition.left, right = logoPosition.right, bottom = logoPosition.bottom } = position;
  let qaAssistantHeight;

  try {
    qaAssistantHeight = window.sessionStorage.getItem('QAAssistantHeight');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // Ignore error
  }

  return `
  @keyframes appear {
    0% {
      opacity: 0;
      display: none;
      height:30px;
      width:30px;
      padding:0;
    }
    1% {
      pointer-events: all;
      position: fixed;
      top: unset;
      left: 24px;
      right: unset;
      bottom: calc(${qaAssistantHeight || '500px'} - 48px);
      display: block;
    }
    50% {
      pointer-events: all;
      height:30px;
      width:30px;
      padding:0;
      left: 24px;
      right: unset;
      bottom: calc(${qaAssistantHeight || '500px'} - 48px);
      opacity: 0;
    }
    55% {
      opacity: 1;
    }
    100% {
      pointer-events: all;
      height:60px;
      width:60px;
      padding:4px;
      position: fixed;
      top: unset;
      left: ${left || 'unset'};
      right: ${right || 'unset'};
      bottom: ${bottom || '48px'};
      opacity: 1;
      display: block;
    }`;
};

export const resetAnimationBubble = () => {
  const animationStyle = document.getElementById('animationAppearStyle');
  if (animationStyle) {
    animationStyle.innerHTML = getAppearAnimation();
  }
};
